import { useCallback, useEffect, useRef, useState } from "react";
import type { ReactElement, ReactNode } from "react";
import { ApiError } from "@kundeninfo/api-client";
import type { TeamKurz, TicketDetail, TicketSpalte, Unternehmen } from "@kundeninfo/api-client";
import { Arbeitsdialog, Feld, darfAbbrechen, useBestaetigung, useFormularZustand, useTelefon } from "@kundeninfo/ui";
import { Formular, Hinweis, Reiter } from "@kundeninfo/ui/me-design";
import { KiKnopf, KiRegister } from "../../components/meHuellen.js";
import { ObjektVerweis } from "../../components/objektPanel/ObjektVerweis.js";
import { VerlaufReiter, KundenReiter, AnhaengeReiter } from "./TicketDialogTeile.js";
import { SchreibHinweis, useSchreibweg } from "./useSchreibweg.js";
import { HIERARCHY_TEXT } from "./hierarchyTexts.js";
import { accessLossText, ownerFromValue, ownerValue } from "./hierarchy.js";
import { confirmOpenChildren } from "./openChildren.js";
import { TicketSteckbrief } from "./TicketSteckbrief.js";
import { SubtaskSection } from "./SubtaskSection.js";
import { EffortSection } from "./EffortSection.js";
import { hoursInput, parseHours, roundQuarter } from "./effort.js";
import { EFFORT_TEXT } from "./effortTexts.js";
import type { Kopf } from "./TicketSteckbrief.js";
import type { TicketsClient } from "./ticketsClient.js";

/**
 * DER ARBEITSDIALOG EINES TICKETS (Spec „Arbeitsdialog des Tickets", F41; seit Paket 1c F121):
 * am Desktop das Ticketpanel rechts über dem Board (`Arbeitsdialog panel="ticket"`: nach Inhalt
 * 640–960 px, verstellbar, je Gerät gemerkt, unter 1024 px vollflächig), Pfad über dem Steckbrief,
 * Kreuz immer sichtbar. Nach jeder Sofort-Schreibhandlung meldet `onGeaendert` nach oben — das
 * Board dahinter lädt neu (Entscheid 194). Speichern/Abbrechen im Fuß gilt nur für Titel,
 * Beschreibung, Team, Eigentümer, Zuständig, Spalte, Wiedervorlage, Fällig bis und Schätzung („Kopf“); Kommentare, Teilaufgaben
 * (ein Abschnitt, kein Reiter, Aufgabe 17), „Lösen“, Kunden, Bezüge und Anhänge sind sofort gespeichert
 * (`useSchreibweg`, die Ticket-Version bleibt dabei stehen, Aufgabe 7). Ebenso Buchungen im Abschnitt „Aufwand“ (Spec
 * Aufwand A19, P3): Die Summen der Antwort gelten sofort, danach holt der Dialog das Detail einmal nach (Verlauf).
 *
 * 409 beim Speichern (Review-Fokus 2): Der Dialog holt den Serverstand sofort nach (Steckbrief
 * und Verlauf zeigen ihn), die EIGENEN Kopfänderungen bleiben stehen, das Formular steht im
 * „fremden Stand“ mit „Das Ticket wurde inzwischen geändert“. „Neu laden“ übernimmt den Stand
 * ganz, „Meine Eingaben behalten“ behält die eigenen Änderungen auf dem neuen Stand — kein
 * stiller Überschreiber.
 *
 * Startversion (Review 15, Lost Update): Solange der Kopf ungespeicherte Änderungen hat, bleibt
 * die Version, auf der die Bearbeitung begann (`basis`), stehen — auch wenn eine Unterobjekt-
 * Antwort oder ein Nachladen ein neueres Detail bringt. Gespeichert wird mit DIESER Version;
 * hat jemand anders den Kopf inzwischen gespeichert, antwortet der Server mit 409 und der
 * Merge-Weg oben greift. Ohne Kopfänderungen folgt die Basis dem Server.
 *
 * Die Entwürfe „Neuer Kommentar“ und „Neue Teilaufgabe“ (Titelfeld der Schnellanlage, Aufgabe 17)
 * leben hier (nicht im Reiter): Sie überstehen den Reiterwechsel und zählen für die Rückfrage
 * beim Abbrechen mit.
 *
 * Der Steckbrief (Kennwerte, Wiedervorlage mit Schnellknöpfen und Uhrzeit F93) steht in `TicketSteckbrief.tsx`.
 *
 * Beobachten (F95, Entscheid 184) ist keine Kopfänderung: Selbst beobachten, andere eintragen
 * (Personen aus `listTicketAssignees` — `/api/verwaltung/users` ist nur für Admins) und austragen
 * laufen über einen eigenen Sofort-Schreibweg wie Kommentare; die Startversion bleibt (Review 15),
 * Fehler stehen als me-hinweis im Slot `meldungen`.
 */

const KOPF_FELDER = ["title", "descriptionMarkdown", "assigneeUserId", "columnId", "resubmitDate", "resubmitTime",
                     "teamId", "owner", "dueDate", "estimate"] as const;

function kopfAus(d: TicketDetail): Kopf {
  return { title: d.title, descriptionMarkdown: d.descriptionMarkdown ?? "", assigneeUserId: d.assignee?.id ?? "",
           columnId: d.columnId, resubmitDate: d.resubmitDate ?? "", resubmitTime: d.resubmitTime ?? "",
           teamId: d.team?.id ?? "", owner: ownerValue(d.owner), dueDate: d.dueDate ?? "",
           // P7: ohne Tausenderpunkt — sonst läse parseHours("1.500") 1,5 h (Review Focus 1).
           estimate: hoursInput(d.estimatedMinutes) };
}

/** Eigene Änderungen (gegenüber `basis`) auf einen neuen Serverstand legen: was der Benutzer
 *  angefasst hat, bleibt; alles andere folgt dem Server. */
function zusammenfuehren(basis: Kopf, eigen: Kopf, neu: Kopf): Kopf {
  const ergebnis = { ...neu };
  for (const f of KOPF_FELDER) if (eigen[f] !== basis[f]) ergebnis[f] = eigen[f];
  return ergebnis;
}

type Stand = { d: TicketDetail; kopf: Kopf; basis: { version: number; kopf: Kopf } };
const istGeaendert = (s: Stand): boolean => KOPF_FELDER.some((f) => s.kopf[f] !== s.basis.kopf[f]);

/** Wie ein neuer Serverstand übernommen wird: `ersetzen` (Laden, „Neu laden“) verwirft die
 *  Kopfänderungen; `teile` (Unterobjekt-Antwort) lässt Kopf UND Startversion stehen, solange
 *  der Kopf geändert ist; `behalten` (409, „Meine Eingaben behalten“) legt die eigenen
 *  Änderungen auf den neuen Stand und rückt die Basis vor. */
type Uebernahme = "ersetzen" | "teile" | "behalten";

export type Entwuerfe = { kommentar: string; teilaufgabe: string };

export type TicketDialogProps = {
  client: TicketsClient; ticketId: string; companies: readonly Unternehmen[];
  /** `supplierView`: LEAD/ADMIN sehen Lieferantendaten (Bezugswahl nennt Verträge und Eingangsrechnungen, Aufgabe 22). */
  ich: { id: string; istAdmin: boolean; supplierView?: boolean };
  onSchliessen: (geaendert: boolean) => void;
  /** Der Pfad über dem Steckbrief — je Herkunft „Tickets › Board“ (Vorgabe) oder „Tickets › Liste“. */
  pfad?: string;
  /** Meldet, ob Ungespeichertes im Dialog steht (Kopfänderungen oder Entwürfe) — für den
   *  Verlassen-Wächter des Bereichs (Review 17). */
  onUngespeichert?: (ungespeichert: boolean) => void;
  /** Nach jeder erfolgreichen Sofort-Schreibhandlung (Kommentar, Teilaufgabe, Kunde, Bezug, Anhang,
   *  Verknüpfung, Beobachten) — der Bereich lädt Board bzw. Liste dahinter neu (F121, 194). */
  onGeaendert?: () => void;
  zusatzReiter?: { id: string; label: string;
    inhalt: (d: TicketDetail, neu: (d: TicketDetail) => void, nachladen: () => Promise<TicketDetail>) => ReactNode }[];
  /** Nebendialog (Spec Teilaufgaben R23, Festlegung 24; Öffnen statt Springen F14): seitlich neben dem Dialog darunter. */
  unter?: "seitlich";
  /** Kennung des Ticketdialogs darunter — nennt dieser Dialog es (Eltern- oder Kind-Ticket), schließt er nur sich (F14). */
  belowId?: string | null;
  /** Ein anderes Ticket öffnen: ein Kind oder das Elternticket — der Bereich legt es seitlich daneben (F14). */
  onOpenTicket?: (id: string) => void;
  /** Objektpanel im Gastgeber („Öffnen statt Springen“, Entscheid 696): die Zurück-Taste schließt es (`me-dialog verlauf`). */
  verlauf?: boolean;
  /** Der Sprungknopf des Objektpanels links unten (Slot `fuss-links`, F11) — in jedem Zustand, auch beim Laden (F13). */
  verweis?: ReactNode;
  /** „Im Board zeigen“ (R25): Fokus auf dieses Ticket im Board. */
  onShowInBoard?: (id: string) => void;
  /** Zählt hoch, wenn ein Kind-Dialog schrieb — der Dialog lädt nach, ohne Kopf und Scrollposition zu verlieren. */
  reloadToken?: number;
  /** Ein Hinweis über dem Dialog, warum er aufging (Terminebene H10: das vorhandene Ticket einer Fälligkeit nach 409). */
  notice?: string;
};

export function TicketDialog(p: TicketDialogProps): ReactElement {
  const [stand, setStand] = useState<Stand | null>(null);
  const [entwuerfe, setEntwuerfe] = useState<Entwuerfe>({ kommentar: "", teilaufgabe: "" });
  const [spalten, setSpalten] = useState<TicketSpalte[]>([]);
  const [leute, setLeute] = useState<{ id: string; name: string; email: string | null }[]>([]);
  /** Für welches Team `leute` geladen ist ("" = alle) — der Hinweis „verlieren den Zugriff" rechnet nur damit. */
  const [leuteTeam, setLeuteTeam] = useState<string | null>(null);
  const [teams, setTeams] = useState<TeamKurz[]>([]);
  /** Alle aktiven Personen (7-3): Der Hinweis nennt nur aktive — gesperrte haben ohnehin keinen Zugang, und ob ein
   *  gesperrtes Mitglied das Team sieht, verrät die Auswahl der Zuständigen nicht (sie führt nur aktive). */
  const [aktive, setAktive] = useState<ReadonlySet<string> | null>(null);
  const [reiter, setReiter] = useState("verlauf");
  const [geaendertSeitOeffnen, setGeaendertSeitOeffnen] = useState(false);
  const [ladefehler, setLadefehler] = useState<string | null>(null);
  /** 410 (R14): die Teilaufgabe wurde zurückgenommen — der Hinweis des Servers und der Weg zum Elternticket. */
  const [zurueckgenommen, setZurueckgenommen] = useState<{ text: string; parentId: string; parentLabel: string } | null>(null);
  /** Befund eines Sofort-Wegs im Kopf („Lösen“, R23). */
  const [kopfMeldung, setKopfMeldung] = useState<string | null>(null);
  const eingabenBehalten = useRef(false);
  const pfad = p.pfad ?? "Tickets › Board";
  // Am Telefon steht der Steckbrief anfangs eingeklappt (Sichtprobe 6): der Reiterinhalt ist ohne
  // Rollen im Bild, der Falt-Knopf des Elements zeigt die Kennwerte.
  const telefon = useTelefon();
  const [kopfZu, setKopfZu] = useState(telefon);

  const uebernehme = useCallback((neu: TicketDetail, wie: Uebernahme) => {
    setStand((s) => {
      const frisch: Stand = { d: neu, kopf: kopfAus(neu), basis: { version: neu.version, kopf: kopfAus(neu) } };
      if (s === null || wie === "ersetzen") return frisch;
      if (wie === "teile") return istGeaendert(s) ? { ...s, d: neu } : frisch;
      return { ...frisch, kopf: zusammenfuehren(s.basis.kopf, s.kopf, kopfAus(neu)) };
    });
  }, []);

  const holen = useCallback(() => p.client.getTicket(p.ticketId), [p.client, p.ticketId]);
  /** Für die Reiter (409 eines Unterobjekts): Die Antwort geht über `teileNeu`, also ohne Verlust
   *  der Kopfänderungen und ihrer Startversion. */
  const nachladen = holen;

  useEffect(() => {
    let aktiv = true;
    holen().then((neu) => { if (aktiv) { uebernehme(neu, "ersetzen"); setLadefehler(null); } })
      .catch((e: unknown) => {
        if (!aktiv) return;
        if (e instanceof ApiError && e.status === 410 && e.withdrawn !== undefined) {
          setZurueckgenommen({ text: e.message, parentId: e.withdrawn.parentId,
            parentLabel: `${e.withdrawn.parentTicketNumber} ${e.withdrawn.parentTitle}` });
          return;
        }
        setLadefehler(e instanceof ApiError && e.status === 404 ? "Ticket nicht gefunden."
          : "Das Ticket konnte nicht geladen werden.");
      });
    return () => { aktiv = false; };
  }, [holen, uebernehme]);
  // Ein Kind schrieb (R23): nachladen wie eine Unterobjekt-Antwort — Kopfänderungen und Scrollposition bleiben.
  useEffect(() => {
    if ((p.reloadToken ?? 0) > 0) void holen().then((neu) => uebernehme(neu, "teile")).catch(() => undefined);
  }, [p.reloadToken, holen, uebernehme]);
  useEffect(() => {
    void p.client.listTicketColumns().then((r) => setSpalten(r.items)).catch(() => undefined);
    void p.client.listMeineTeams().then((r) => setTeams(r.items)).catch(() => undefined);
  }, [p.client]);
  // Zuständig und Beobachter folgen dem GEWÄHLTEN Team (212): ohne Team alle aktiven, sonst wer es sieht.
  // Fixrunde 1 (7-2): Eine UNGESPEICHERT geänderte Wahl, die das neue Team nicht sieht, fällt zurück auf den
  // gespeicherten Zuständigen, wenn er Mitglied ist, sonst auf „Niemand“ — sonst stünde ein Wert ohne Option da
  // und „Speichern“ endete in 422 TICKET_NICHT_IM_TEAM. Die gespeicherte Wahl bleibt unberührt (der Server leert
  // sie beim Wechsel, der Hinweis nennt sie).
  const teamWahl = stand?.kopf.teamId ?? "";
  useEffect(() => {
    let aktiv = true;
    p.client.listTicketAssignees().then((r) => { if (aktiv) setAktive(new Set(r.items.map((u) => u.id))); })
      .catch(() => undefined);
    return () => { aktiv = false; };
  }, [p.client]);
  useEffect(() => {
    let aktiv = true;
    (teamWahl === "" ? p.client.listTicketAssignees() : p.client.listTicketAssignees(teamWahl))
      .then((r) => {
        if (!aktiv) return;
        setLeute(r.items);
        setLeuteTeam(teamWahl);
        if (teamWahl === "") return;
        const sieht = new Set(r.items.map((u) => u.id));
        setStand((s) => {
          if (s === null) return s;
          const wahl = s.kopf.assigneeUserId;
          const gespeichert = s.basis.kopf.assigneeUserId;
          if (wahl === "" || wahl === gespeichert || sieht.has(wahl)) return s;
          return { ...s, kopf: { ...s.kopf, assigneeUserId: gespeichert !== "" && sieht.has(gespeichert) ? gespeichert : "" } };
        });
      })
      .catch(() => {
        // 7-7: Ohne Liste des Teams keine Nichtmitglieder anbieten (sonst erst 422 beim Speichern); kein Hinweis.
        // Fixrunde 2: Eine ungespeichert geänderte Wahl fällt auf die gespeicherte Person zurück (bzw. „Niemand“,
        // wenn keine gespeichert ist) — sie stünde sonst ohne Option da; die gespeicherte prüft der Server.
        if (!aktiv || teamWahl === "") return;
        setLeute([]);
        setLeuteTeam(null);
        setStand((s) => (s === null || s.kopf.assigneeUserId === s.basis.kopf.assigneeUserId ? s
          : { ...s, kopf: { ...s.kopf, assigneeUserId: s.basis.kopf.assigneeUserId } }));
      });
    return () => { aktiv = false; };
  }, [p.client, teamWahl]);

  const d = stand?.d ?? null;
  const kopf = stand?.kopf ?? null;
  const geaendert = stand !== null && istGeaendert(stand);
  const entwurfOffen = entwuerfe.kommentar.trim() !== "" || entwuerfe.teilaufgabe.trim() !== "";
  const ungespeichert = geaendert || entwurfOffen;
  const onUngespeichert = p.onUngespeichert;
  useEffect(() => { onUngespeichert?.(ungespeichert); }, [onUngespeichert, ungespeichert]);
  const aendereKopf = (teil: Partial<Kopf>) => setStand((s) => (s === null ? s : { ...s, kopf: { ...s.kopf, ...teil } }));

  const formular = useFormularZustand({
    speichern: async () => {
      if (stand === null) return undefined;
      const { d: aktuell, kopf, basis } = stand;
      const alt = basis.kopf;
      const patch: Record<string, unknown> = {};
      if (kopf.title !== alt.title) patch.title = kopf.title;
      if (kopf.descriptionMarkdown !== alt.descriptionMarkdown) patch.descriptionMarkdown = kopf.descriptionMarkdown === "" ? null : kopf.descriptionMarkdown;
      if (kopf.assigneeUserId !== alt.assigneeUserId) patch.assigneeUserId = kopf.assigneeUserId === "" ? null : kopf.assigneeUserId;
      if (kopf.columnId !== alt.columnId) patch.columnId = kopf.columnId;
      if (kopf.resubmitDate !== alt.resubmitDate) patch.resubmitDate = kopf.resubmitDate === "" ? null : kopf.resubmitDate;
      if (kopf.resubmitTime !== alt.resubmitTime) {
        patch.resubmitTime = kopf.resubmitTime === "" ? null : kopf.resubmitTime;
      }
      if (kopf.teamId !== alt.teamId) patch.teamId = kopf.teamId === "" ? null : kopf.teamId;
      if (kopf.owner !== alt.owner) Object.assign(patch, ownerFromValue(kopf.owner));
      if (kopf.dueDate !== alt.dueDate) patch.dueDate = kopf.dueDate === "" ? null : kopf.dueDate;
      // A18: nur eine geänderte Schätzung geht mit (Review Focus 1), immer als ganze Viertelstunden-Minuten (Review H2).
      if (kopf.estimate !== alt.estimate) {
        const m = parseHours(kopf.estimate);
        if (m === undefined) return undefined; // Unlesbares speichert nie; der Befund steht am Feld.
        patch.estimatedMinutes = m === null ? null : roundQuarter(m);
      }
      try {
        // Die Startversion der Bearbeitung, nicht die des zuletzt gesehenen Details (Review 15).
        return await p.client.updateTicket(aktuell.id, basis.version, patch);
      } catch (e) {
        // Den Serverstand sofort nachholen, die eigenen Eingaben bleiben (Review-Fokus 2).
        if (e instanceof ApiError && e.status === 409) {
          try { uebernehme(await holen(), "behalten"); } catch { /* der Hinweis bietet „Neu laden“ */ }
        }
        throw e;
      }
    },
    neuLaden: async () => {
      const behalten = eingabenBehalten.current;
      eingabenBehalten.current = false;
      uebernehme(await holen(), behalten ? "behalten" : "ersetzen");
    },
    bekannteFelder: [...KOPF_FELDER.filter((f) => f !== "owner" && f !== "estimate"), "ownerUserId", "ownerTeamId",
                     "estimatedMinutes"],
    beschriftungen: { title: "Titel", descriptionMarkdown: "Beschreibung", assigneeUserId: "Zuständig",
                      columnId: "Status", resubmitDate: "Wiedervorlage", resubmitTime: "Uhrzeit", teamId: "Team",
                      ownerUserId: HIERARCHY_TEXT.owner, ownerTeamId: HIERARCHY_TEXT.owner, dueDate: HIERARCHY_TEXT.dueDate,
                      estimatedMinutes: EFFORT_TEXT.estimate },
  });
  const gesperrt = formular.zustand !== "bereit";
  const { bestaetigen } = useBestaetigung();

  async function speichern(): Promise<void> {
    // A18: Eine unlesbare Schätzung speichert nicht — der Befund steht am Feld, keine Rückfrage davor; der Steckbrief
    // klappt auf, damit er nicht unsichtbar bleibt (Review G2).
    if (stand !== null && parseHours(stand.kopf.estimate) === undefined) { setKopfZu(false); return; }
    // E440: Der Teamwechsel am obersten Ticket nimmt den Teilbaum mit — erst nach der Nachfrage; „Abbrechen“ ändert nichts.
    if (stand !== null && stand.d.parent === null && stand.d.descendantCount > 0 && stand.kopf.teamId !== stand.basis.kopf.teamId
        && !(await bestaetigen({ titel: HIERARCHY_TEXT.teamSubtreeTitle(stand.d.descendantCount),
                                 bestaetigenText: HIERARCHY_TEXT.teamSubtreeConfirm }))) return;
    // E435: Status in eine DONE-Spalte mit offenen Kindern erst nach der Rückfrage; „Weiter bearbeiten“ speichert nicht.
    const ziel = spalten.find((s) => s.id === stand?.kopf.columnId);
    if (stand !== null && ziel?.stateKind === "DONE" && stand.d.stateKind !== "DONE"
        && !(await confirmOpenChildren(bestaetigen, stand.d.subtasks, (id) => spalten.find((s) => s.id === id)?.name ?? ""))) return;
    const ergebnis = await formular.aktion();
    if (ergebnis !== undefined) p.onSchliessen(true);
  }

  async function abbrechen(): Promise<void> {
    if (formular.laeuft) return;
    if (!(await darfAbbrechen(ungespeichert))) return;
    p.onSchliessen(geaendertSeitOeffnen);
  }

  const onGeaendert = p.onGeaendert;
  const teileNeu = useCallback((neu: TicketDetail) => {
    uebernehme(neu, "teile");
    setGeaendertSeitOeffnen(true);
    onGeaendert?.();
  }, [uebernehme, onGeaendert]);
  const beobachten = useSchreibweg<TicketDetail>(teileNeu, { nachladen });
  /** P3: Summen der Buchung sofort, danach das Detail einmal nach (Verlauf) — Kopf und Startversion bleiben (A15). */
  const aufwandNeu = useCallback((neu: TicketDetail) => {
    teileNeu(neu);
    void holen().then((frisch) => uebernehme(frisch, "teile"), () => undefined);
  }, [teileNeu, holen, uebernehme]);

  /** Ein anderes Ticket (Eltern- oder Kind-Ticket): liegt es schon darunter, schließt dieser Dialog (F14), sonst der Weg des
   *  Bereichs bzw. Gastgebers. */
  const oeffneTicket = (id: string): void => {
    if (id === p.belowId) void abbrechen();
    else p.onOpenTicket?.(id);
  };
  // Die Hülle des Panels in beiden Zuständen gleich (ein Element, ein Verlaufseintrag; der Sprung bleibt beim Laden, F13).
  const huelle = { panel: "ticket", ...(p.unter === undefined ? {} : { unter: p.unter }),
    ...(p.verlauf === true ? { verlauf: true } : {}), fussLinks: p.verweis, pfad: <span>{pfad}</span> };
  if (d === null || kopf === null) {
    return (
      <Arbeitsdialog titel="Ticket" {...huelle} onAbbrechen={() => p.onSchliessen(false)}>
        {zurueckgenommen !== null ? (
          // C2: derselbe Verweis auf das Elternticket wie im Steckbrief (K8).
          <Hinweis art="info" aktionen={<ObjektVerweis bezug={{ art: "ticket", id: zurueckgenommen.parentId,
            label: zurueckgenommen.parentLabel }} eigenesPanel={() => oeffneTicket(zurueckgenommen.parentId)} />}>
            {zurueckgenommen.text}
          </Hinweis>
        ) : ladefehler !== null ? <Hinweis art="fehler">{ladefehler}</Hinweis> : <p>Lädt …</p>}
      </Arbeitsdialog>
    );
  }

  // Zum Eintragen: wer noch nicht beobachtet; sich selbst trägt man über „Beobachten“ ein.
  const kandidaten = leute.filter((u) => u.id !== p.ich.id && !d.watchers.some((w) => w.id === u.id));
  const auswahlLeute = d.assignee !== null && !leute.some((u) => u.id === d.assignee!.id) ? [d.assignee, ...leute] : leute;
  // 213: Wer beim Speichern den Zugriff verliert — gerechnet aus den Zuständigen des NEUEN Teams (erst, wenn sie da sind).
  const verlierer = kopf.teamId === "" || kopf.teamId === (d.team?.id ?? "") || leuteTeam !== kopf.teamId || aktive === null
    ? null : accessLossText(d, kopf.assigneeUserId, auswahlLeute, new Set(leute.map((u) => u.id)), aktive);

  const reiterListe = [
    { id: "verlauf", label: "Verlauf" }, { id: "kunden", label: "Kunden und Bezüge" }, { id: "anhaenge", label: "Anhänge" },
    ...(p.zusatzReiter ?? []).map((r) => ({ id: r.id, label: r.label })),
  ];

  const wer = formular.fremderStand?.changedBy;
  const fremderHinweis = formular.zustand !== "fremder-stand" ? undefined : (
    <>
      {`Das Ticket wurde inzwischen geändert${wer !== undefined && wer !== "" ? ` (von ${wer})` : ""}. `
        + "Deine Eingaben stehen noch da — „Neu laden“ übernimmt den aktuellen Stand, "
        + "„Meine Eingaben behalten“ legt sie auf den neuen Stand."}
      <KiKnopf art="leise" onClick={() => { eingabenBehalten.current = true; void formular.neuLaden(); }}>
        Meine Eingaben behalten
      </KiKnopf>
    </>
  );

  return (
    <Arbeitsdialog titel={`${d.ticketNumber} ${d.title}`} {...huelle}
      onAbbrechen={() => { void abbrechen(); }} abbrechenGesperrt={formular.laeuft}
      steckbrief={(
        <TicketSteckbrief d={d} kopf={kopf} companies={p.companies} ichId={p.ich.id} client={p.client} spalten={spalten}
          teams={teams} leute={auswahlLeute} kandidaten={kandidaten} gesperrt={gesperrt} feldFehler={formular.feldFehler}
          feldRaeumen={formular.feldFehlerRaeumen} aendereKopf={aendereKopf} eingeklappt={kopfZu} onEingeklappt={setKopfZu}
          onToParent={oeffneTicket}
          onDetached={(k) => {
            setKopfMeldung(null);
            // Nachladen: Verlauf (PARENT_CHANGED) und Teilbaum frisch; ohne Antwort wenigstens ohne „Teil von“.
            void holen().then((neu) => uebernehme(neu, "behalten"),
              () => uebernehme({ ...d, ...k, parent: null }, "behalten"));
            setGeaendertSeitOeffnen(true);
            p.onGeaendert?.();
          }}
          onError={setKopfMeldung} onAnhaenge={() => setReiter("anhaenge")} beobachten={beobachten} />
      )}
      // R21, R22: der Abschnitt „Teilaufgaben“ zwischen Meldungen und Reitern; Personen der Schnellanlage wie „Zuständig“.
      // A19: darunter der Abschnitt „Aufwand“.
      abschnitt={(
        <>
          <SubtaskSection d={d} client={p.client} spalten={spalten} leute={auswahlLeute} ichId={p.ich.id}
            entwurf={entwuerfe.teilaufgabe} onEntwurf={(w) => setEntwuerfe((e) => ({ ...e, teilaufgabe: w }))} onNeu={teileNeu}
            nachladen={nachladen} onOpenChild={oeffneTicket}
            {...(p.onShowInBoard === undefined ? {} : { onShowInBoard: p.onShowInBoard })} />
          <EffortSection d={d} client={p.client} ich={p.ich} onNeu={aufwandNeu} />
        </>
      )}
      register={(
        <KiRegister className="td__register" klebend aria-label="Bereiche des Tickets" aktiv={`td-${reiter}`}
          onReiterGewaehlt={(e) => setReiter(e.id.replace(/^td-/, ""))}>
          {reiterListe.map((r) => <Reiter key={r.id} fuer={`td-${r.id}`} beschriftung={r.label} />)}
        </KiRegister>
      )}
      // Der Titel gehört zum Kopf über den Reitern (Sichtprobe 7): Slot `meldungen` steht zwischen
      // Steckbrief und Register, mit ihm der fremde Stand und die Meldungen des Kopfs.
      meldungen={(
        <div className="td__kopffeld">
          {p.notice !== undefined && <Hinweis art="info">{p.notice}</Hinweis>}
          <Formular beschriftung="oben" {...formular.formularProps} hinweis={fremderHinweis}>
            <Feld id="td-titel" label="Titel" wert={kopf.title} onChange={(w) => aendereKopf({ title: w })}
              bearbeitbar pflicht max={200} fehler={formular.feldFehler.title} />
          </Formular>
          {formular.meldung !== null && <Hinweis art="fehler">{formular.meldung}</Hinweis>}
          {kopfMeldung !== null && <Hinweis art="fehler">{kopfMeldung}</Hinweis>}
          {verlierer !== null && <Hinweis art="warnung">{verlierer}</Hinweis>}
          <SchreibHinweis meldung={beobachten.meldung} />
          {formular.zustand === "bereit" && stand?.basis.version !== d.version && geaendert && (
            <Hinweis art="warnung">
              Das Ticket wurde inzwischen geändert. Deine Änderungen am Kopf beziehen sich auf den früheren
              Stand — beim Speichern zeigt sich, ob sie sich vertragen.
            </Hinweis>
          )}
        </div>
      )}
      hinweis={<span>Kommentare, Teilaufgaben, Kunden, Bezüge und Anhänge sind sofort gespeichert.</span>}
      fuss={(
        <>
          <KiKnopf art="primaer" deaktiviert={gesperrt || !geaendert} onClick={() => { void speichern(); }}>Speichern</KiKnopf>
          <KiKnopf deaktiviert={formular.laeuft} onClick={() => { void abbrechen(); }}>Abbrechen</KiKnopf>
        </>
      )}>
      <div role="tabpanel" className="td__tafel" id={`td-${reiter}`} aria-label={reiterListe.find((r) => r.id === reiter)?.label}>
        {reiter === "verlauf" && (
          <VerlaufReiter d={d} client={p.client} ich={p.ich} onNeu={teileNeu} nachladen={nachladen}
            beschreibung={kopf.descriptionMarkdown} beschreibungGesperrt={gesperrt}
            beschreibungFehler={formular.feldFehler.descriptionMarkdown}
            onBeschreibung={(w) => { formular.feldFehlerRaeumen("descriptionMarkdown"); aendereKopf({ descriptionMarkdown: w }); }}
            entwurf={entwuerfe.kommentar} onEntwurf={(w) => setEntwuerfe((e) => ({ ...e, kommentar: w }))} />
        )}
        {reiter === "kunden" && (
          <KundenReiter d={d} client={p.client} companies={p.companies} onNeu={teileNeu} nachladen={nachladen}
            supplierView={p.ich.supplierView === true} />
        )}
        {reiter === "anhaenge" && <AnhaengeReiter d={d} client={p.client} onNeu={teileNeu} nachladen={nachladen} />}
        {(p.zusatzReiter ?? []).filter((r) => r.id === reiter).map((r) => (
          <div key={r.id}>{r.inhalt(d, teileNeu, nachladen)}</div>
        ))}
      </div>
    </Arbeitsdialog>
  );
}
